import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useTranslation } from '@cobliteam/i18n';
import { isFilled } from '@cobliteam/prismic';
import { PrismicRichText } from '@cobliteam/prismic/src/components';
import type { RichTextSliceDefaultPrimary } from '@cobliteam/prismic/src/types/support-cobli.generated';
import Divider from '@cobliteam/ui/components/Divider';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { motion } from 'framer-motion';

interface PageContentProps {
  icon?: IconNames;
  title: string;
  resume: any;
  content?: RichTextSliceDefaultPrimary;
}

const PageContent = ({ icon, title, resume, content }: PageContentProps) => {
  const { openBlipChat } = useChatWidget();
  const { t } = useTranslation('business-manager');

  const handleContactSupport = () => {
    openBlipChat();
  };

  if (!(title || resume || content)) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center p-8'>
        <Typography variant='body' className='text-gray-11'>
          {t('help_center.cards.content_unavailable.title')}
        </Typography>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: 'easeOut',
        delay: 0.2,
      }}
    >
      <motion.div className='flex flex-1 flex-col space-y-6 p-4' initial='initial' animate='animate'>
        <motion.div className='flex items-center gap-3'>
          {icon && (
            <div className='rounded-md bg-primary-3 p-2'>
              <Icon name={icon} size='md' className='text-primary-9' />
            </div>
          )}
          <Typography variant='heading-xl'>{title || 'Título não disponível'}</Typography>
        </motion.div>

        <motion.div>
          <PrismicRichText field={resume} className='text-gray-12' />
        </motion.div>

        <motion.div className='prose prose-sm max-w-none space-y-4 text-gray-12 [&>em]:text-gray-11 [&>em]:italic [&>h1]:mt-6 [&>h1]:mb-4 [&>h1]:text-gray-12 [&>h2]:mt-6 [&>h2]:mb-4 [&>h2]:text-gray-12 [&>h3]:mt-5 [&>h3]:mb-3 [&>h3]:text-gray-12 [&>li]:pl-2 [&>li]:text-gray-11 [&>li]:leading-relaxed [&>ol>li]:ml-6 [&>ol>li]:list-decimal [&>ol>li]:marker:text-primary-9 [&>ol]:my-6 [&>ol]:space-y-3 [&>p]:mb-4 [&>p]:text-gray-11 [&>p]:leading-relaxed [&>strong]:font-semibold [&>strong]:text-gray-12 [&>ul>li]:ml-6 [&>ul>li]:list-disc [&>ul>li]:marker:text-primary-9 [&>ul]:my-6 [&>ul]:space-y-3 [&_em]:text-gray-11 [&_em]:italic [&_strong]:font-semibold [&_strong]:text-gray-12'>
          {isFilled.richText(content?.richtext) && <PrismicRichText field={content?.richtext} />}
        </motion.div>

        <Divider />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.4,
            ease: 'easeOut',
            delay: 0.4,
          }}
          className='flex flex-col items-center gap-1 py-2'
        >
          <Typography variant='text-md' className='text-gray-12'>
            {t('help_center.cards.not_finding.title')}
          </Typography>
          <Typography variant='text-md' className='cursor-pointer text-primary-9 hover:text-primary-10 hover:underline' onClick={handleContactSupport}>
            {t('quick_actions.support_contact')}
          </Typography>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default PageContent;
